import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

import type { IResourceDatabaseRecord, IResourceViewProps } from '@/api/types';
import type { ITableSortingState } from '../table/types';

import {
  DropdownMenu,
  ResourceTableCell,
  ResourceTableHeader,
  ResourceTableHeaderCell,
  ResourceTableRow,
  Table,
} from '@/components/common';
import { ResourceDatabaseFieldTypeId } from '@/api/enums';
import { ResourceDatabaseItemValue } from './ResourceDatabaseItemValue';
import { useDebouncedResizeObserver } from '@/hooks';
import { DEBOUNCE_RESIZE_OBSERVER_DELAY, ID_COLUMN_NAME } from '@/config/variables';

const getMultiline = (columnType: ResourceDatabaseFieldTypeId): number =>
  [
    ResourceDatabaseFieldTypeId.TextMultiline,
    ResourceDatabaseFieldTypeId.WebLink,
    ResourceDatabaseFieldTypeId.FileAttachment,
  ].includes(columnType)
    ? 2
    : 1;

interface IProps {
  items: IResourceDatabaseRecord[];
  sortingState: ITableSortingState<string>;
  onSort: (columnName: string) => void;
  selectedItems: number[];
  toggleItem: (item: number) => void;
  toggleAll: () => void;
  showBulkSelect: boolean;
  columnsToShow: IResourceViewProps[];
  sortableColumns: IResourceViewProps[];
  openPreview: (item: IResourceDatabaseRecord) => void;
  openEditItemModal: (item: IResourceDatabaseRecord) => void;
  openDeleteItemModal: (itemId: number) => void;
  getAccessType: (item: IResourceDatabaseRecord) => 'view' | 'edit' | 'delete';
}

const ResourceDatabaseItemsTable = ({
  items,
  sortingState,
  onSort,
  selectedItems,
  toggleItem,
  toggleAll,
  showBulkSelect,
  columnsToShow,
  openEditItemModal,
  openDeleteItemModal,
  openPreview,
  getAccessType,
  sortableColumns,
}: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const fields = [...columnsToShow].sort((a, b) => a.position - b.position).map(item => item.field);
  const tableRef = useRef<HTMLDivElement | null>(null);
  const [isTableOverflow, setIsTableOverflow] = useState(false);

  const getDropdownOptions = useCallback(
    (item: IResourceDatabaseRecord) => {
      const options = [
        {
          label: t`resources.content.openPreview`,
          callback: () => openPreview(item),
        },
      ];

      switch (getAccessType(item)) {
        case 'view':
          break;
        case 'delete':
          options.push({
            label: t`common.btnDelete`,
            callback: () => openDeleteItemModal(item.Id),
          });
        case 'edit':
          options.splice(1, 0, {
            label: t`common.btnEdit`,
            callback: () => openEditItemModal(item),
          });
      }

      return options;
    },
    [getAccessType, openDeleteItemModal, openEditItemModal, openPreview, t],
  );

  const handleTableOverflow = useCallback(() => {
    const scrollWidth = tableRef.current?.scrollWidth;
    const rect = tableRef.current?.getBoundingClientRect();

    if (scrollWidth && rect) {
      if (scrollWidth > rect.width) return setIsTableOverflow(true);
      setIsTableOverflow(false);
    }
  }, []);

  useEffect(() => {
    handleTableOverflow();
  }, [items, handleTableOverflow]);

  const onResize = useCallback(() => {
    handleTableOverflow();
  }, [handleTableOverflow]);

  const { ref: resizerRef } = useDebouncedResizeObserver({
    wait: DEBOUNCE_RESIZE_OBSERVER_DELAY,
    onResize,
  });

  return (
    <Table
      type="floating"
      tableRef={ref => {
        tableRef.current = ref;
        resizerRef(ref);
      }}
    >
      <StyledResourceTableHeader className="small">
        {showBulkSelect && (
          <SelectAllHeaderCell minWidth="22px">
            <input type="checkbox" checked={selectedItems.length === items.length} onChange={toggleAll} />
          </SelectAllHeaderCell>
        )}
        {fields.map((field, id) => {
          const isSortable = sortableColumns.some(sortable => sortable.field.id === field.id);
          return (
            <StyledHeaderCell
              key={id}
              size={1}
              sortingEnabled={isSortable}
              sortingState={sortingState[field.columnName]}
              onClick={() => isSortable && onSort(field.columnName)}
              $isTableOverflow={isTableOverflow}
              minWidth="218px"
            >
              {field.name}
            </StyledHeaderCell>
          );
        })}
      </StyledResourceTableHeader>
      {items.map((item, id) => (
        <StyledResourceTableRow className="small" key={id} type="floating" firstBgColor="#f9fAfb" secondBgColor="#fff">
          {showBulkSelect && (
            <SelectRowCell minWidth="22px">
              <input
                type="checkbox"
                checked={selectedItems.some(selected => selected === item.Id)}
                onChange={() => toggleItem(item.Id)}
              />
            </SelectRowCell>
          )}
          {fields.map((field, cellId) => (
            <StyledRowCell
              key={`${id}_${cellId}`}
              size={1}
              multiline={getMultiline(field.rtxtype_Id)}
              $isTableOverflow={isTableOverflow}
              minWidth="200px"
            >
              <ResourceDatabaseItemValue columnType={field.rtxtype_Id}>
                {/* For some reason id column name is in properties instead */}
                {item[field.name === ID_COLUMN_NAME ? field.properties : field.name]}
              </ResourceDatabaseItemValue>
            </StyledRowCell>
          ))}
          <ResourceTableCell width="24px" minWidth="24px" className="dropdown-action">
            <DropdownMenu options={getDropdownOptions(item)} />
          </ResourceTableCell>
        </StyledResourceTableRow>
      ))}
    </Table>
  );
};

export { ResourceDatabaseItemsTable };

const StyledRowCell = styled(ResourceTableCell)<{ $isTableOverflow: boolean }>`
  ${({ $isTableOverflow }) =>
    $isTableOverflow
      ? css`
          width: 200px;
          min-width: 200px;
          max-width: 200px;
        `
      : css`
          width: fit-content;
        `}
`;

const StyledHeaderCell = styled(ResourceTableHeaderCell)<{ $isTableOverflow: boolean }>`
  ${({ $isTableOverflow }) =>
    $isTableOverflow
      ? css`
          width: 218px;
          min-width: 218px;
          max-width: 218px;
        `
      : css`
          width: fit-content;
        `}
`;

const StyledResourceTableHeader = styled(ResourceTableHeader)`
  padding-right: 45px;
`;

const StyledResourceTableRow = styled(ResourceTableRow)`
  padding-right: 45px;
`;

const SelectAllHeaderCell = styled(ResourceTableHeaderCell)`
  padding: 0;
`;

const SelectRowCell = styled(ResourceTableCell)`
  width: fit-content;
  margin: 0;
`;
